<template>
    <div class="flex flex-col items-center justify-center lg:min-h-[370px]">
        <div class="hidden lg:grid lg:grid-rows-1 gap-1 relative overflow-hidden container mx-auto lg:h-[420px]" :class="grid.length == 1
            ? 'grid-cols-1'
            : images.length == 3
                ? 'lg:grid-cols-3'
                : 'lg:grid-cols-2'
            ">
            <div class="overflow-hidden relative" :class="images.length == 3 ? 'col-span-2' : 'col-span-1'">
                <!-- Left Image — the hero / LCP element: rendered eagerly (in SSR,
                     no lazy fade) and fetched at high priority so it is a valid,
                     fast Largest Contentful Paint candidate. -->
                <lazy-image eager>
                    <template #image="{ load }">
                        <p-image image-class="object-cover w-full h-full"
                            class="block object-cover w-full h-full cursor-pointer" @load="load" @click="openGallery(0)"
                            :src="getImage(0).original" :alt="imageAlt(0)" fetchpriority="high" loading="eager"
                            decoding="async"
                            :preview="images.length <= 5"><template #previewicon><span /></template></p-image>
                    </template>
                </lazy-image>


                <div v-if="video" class="absolute top-0 bottom-0 left-0 right-0 flex items-center justify-center">
                    <video-modal :video="video" />
                </div>
            </div>
            <div v-if="grid[1]" class="grid grid-cols-2 lg:grid-cols-1 items-center justify-center gap-1 z-20">
                <div class="grid items-center justify-center h-full gap-1"
                    :class="grid[1][0] == 1 ? 'grid-cols-1' : 'lg:grid-cols-2'">
                    <lazy-image v-for="(_, i) in Array.from({ length: grid[1][0] })" eager>
                        <template #image="{ load }">
                            <p-image image-class="object-cover w-full h-full" class="object-cover w-full h-full cursor-pointer"
                                @load="load" @click="openGallery(1 + i)" :src="getImage(1 + i).original"
                                :alt="imageAlt(1 + i)" loading="eager" decoding="async"
                                :preview="images.length <= 5"><template #previewicon><span /></template></p-image>
                        </template>
                    </lazy-image>
                </div>

                <div v-if="grid[1][1]" class="grid h-full gap-1"
                    :class="grid[1][1] == 1 ? 'grid-cols-1' : 'grid-cols-2'">
                    <lazy-image v-for="(_, i) in Array.from({ length: grid[1][1] })" eager>
                        <template #image="{ load }">
                            <p-image image-class="object-cover w-full h-full"
                                class="object-cover w-full h-full cursor-pointer" @load="load"
                                :src="getImage(grid[1][0] + i + 1).original" @click="openGallery(grid[1][0] + i + 1)"
                                :alt="imageAlt(grid[1][0] + i + 1)" loading="eager" decoding="async"
                                :preview="images.length <= 5"><template #previewicon><span /></template></p-image>
                        </template>
                    </lazy-image>
                </div>
            </div>

            <div v-if="images.length > 5" class="absolute bottom-3 right-8 z-20">
                <p-button :label="images.length - 5 + ' more'" severity="contrast" size="small"
                    @click="display = true" />
            </div>
        </div>

        <div class="lg:hidden flex items-center justify-center">
            <Galleria :value="images" :circular="true" containerStyle="max-width: 100%" :showItemNavigators="true"
                :showThumbnails="false"
                :pt="{ prevButton: { 'aria-label': 'Previous image' }, nextButton: { 'aria-label': 'Next image' } }">
                <template #previousitemicon><span class="pi pi-chevron-left z-[999]"></span></template>
                <template #nextitemicon><span class="pi pi-chevron-right z-[999]"></span></template>
                <template #item="{ item }">

                    <div class="relative w-full overflow-hidden" style="aspect-ratio: 4/3">
                        <!-- First slide is the mobile hero / LCP element — render it
                             eagerly at high priority; the rest stay lazy. -->
                        <lazy-image :eager="images.indexOf(item) === 0">
                            <template #image="{ load }">
                                <p-image @load="load" class="block w-full h-full"
                                    image-class="object-cover w-full h-full" :src="item.original"
                                    :alt="imageAlt(images.indexOf(item))"
                                    :fetchpriority="images.indexOf(item) === 0 ? 'high' : undefined"
                                    :loading="images.indexOf(item) === 0 ? 'eager' : 'lazy'" decoding="async"
                                    preview><template #previewicon><span /></template></p-image>
                            </template>
                        </lazy-image>

                        <div v-if="video && images[0]?.original == item.original"
                            class="absolute top-0 bottom-0 left-0 right-0 flex items-center justify-center">
                            <video-modal :video="video" />
                        </div>
                    </div>

                </template>
            </Galleria>

        </div>

        <Galleria v-if="images.length > 5" v-model:visible="display" :value="images"
            :responsiveOptions="responsiveOptions" :numVisible="6" container-class="w-[90vw] md:w-[50vw] z-1"
            :circular="true" :fullScreen="true" :showItemNavigators="true" :active-index="activeIndex"
            :pt="{ prevButton: { 'aria-label': 'Previous image' }, nextButton: { 'aria-label': 'Next image' } }">
            <template #previousitemicon><span class="pi pi-chevron-left z-[999]"></span></template>
            <template #nextitemicon><span class="pi pi-chevron-right z-[999]"></span></template>
            <template #item="{ item }">
                <lazy-image>
                    <template #image="{ load }">
                        <p-image @load="load" :src="item.original" :alt="imageAlt(images.indexOf(item))" preview><template
                                #previewicon><span /></template></p-image>
                    </template>
                </lazy-image>
            </template>
            <template #thumbnail="{ item }">
                <lazy-image>
                    <template #image="{ load }">
                        <p-image @load="load" :src="item.original" :alt="imageAlt(images.indexOf(item))" />
                    </template>
                </lazy-image>
            </template>
        </Galleria>
    </div>
</template>

<script setup lang="ts">
import LazyImage from "./LazyImage.vue";
import { Image as PImage } from "primevue";
import { ImageStack, VideoT } from "@/types";
import { ref } from "vue";
import { Button as PButton, Galleria } from "primevue";
import VideoModal from "./VideoModal.vue";

const display = ref(false);

const responsiveOptions = ref([
    {
        breakpoint: "1500px",
        numVisible: 5,
    },
    {
        breakpoint: "1024px",
        numVisible: 3,
    },
    {
        breakpoint: "768px",
        numVisible: 2,
    },
    {
        breakpoint: "560px",
        numVisible: 1,
    },
]);


const activeIndex = ref(0);

const { images, alt } = defineProps<{
    images: ImageStack[];
    video?: VideoT
    alt?: string
}>();

const imageAlt = function (index: number): string {
    const base = alt || "Tour";
    return `${base} – photo ${index + 1} of ${images.length}`;
};

const generateGrid = function (): any {
    switch (images.length) {
        case 1:
            return [1];

        case 2:
            return [1, [1]];

        case 3:
            return [1, [1, 1]];

        case 4:
            return [1, [2, 1]];

        default:
            return [1, [2, 2]];
    }
};

const grid = ref(generateGrid());

const getImage = function (index: number): ImageStack {
    return images[index];
};

const openGallery = function (index: number) {
    if (images.length > 5) {
        activeIndex.value = index;
        display.value = true
    }

}
</script>

<style lang="scss">
.p-overlay-mask {
    /* From https://css.glass */
    background: rgba(23, 23, 23, 0.69) !important;
    box-shadow: 0 4px 30px rgba(0, 0, 0, 0.1) !important;
    backdrop-filter: blur(10.4px) !important;
    -webkit-backdrop-filter: blur(10.4px) !important;
}
</style>
